import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const ORANGE = "#E8470A";
const DARK = "#172033";
const PAGE_BG = "#F5F2EC";

const PublicInfoPage = ({
  eyebrow,
  title,
  intro,
  highlights = [],
  sections = [],
  relatedLinks = [],
  lastUpdated,
}) => {
  return (
    <main style={{ backgroundColor: PAGE_BG, paddingBottom: "80px" }}>

      {/* ── HEADER ── */}
      <div style={{ backgroundColor: DARK, paddingTop: "80px" }}>
        <div style={{ maxWidth: "1140px", margin: "0 auto", padding: "72px 24px 80px" }}>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, ease: "easeOut" }}
          >
            {eyebrow && (
              <span style={{
                display: "inline-block",
                color: ORANGE,
                fontSize: "11px",
                fontWeight: 700,
                letterSpacing: "0.3em",
                textTransform: "uppercase",
                marginBottom: "20px",
              }}>
                {eyebrow}
              </span>
            )}

            <h1 style={{
              fontSize: "clamp(32px, 5vw, 56px)",
              fontWeight: 800,
              color: "#ffffff",
              lineHeight: 1.1,
              margin: "0 0 24px",
              maxWidth: "780px",
            }}>
              {title}
            </h1>

            {intro && (
              <p style={{
                fontSize: "17px",
                lineHeight: 1.75,
                color: "rgba(255,255,255,0.65)",
                margin: "0 0 24px",
                maxWidth: "680px",
              }}>
                {intro}
              </p>
            )}

            {lastUpdated && (
              <span style={{
                fontSize: "13px",
                color: "rgba(255,255,255,0.35)",
              }}>
                Actualizado: {lastUpdated}
              </span>
            )}
          </motion.div>
        </div>
      </div>

      {/* ── CONTENIDO ── */}
      <div style={{ maxWidth: "1140px", margin: "0 auto", padding: "0 24px" }}>

        {/* Highlights */}
        {highlights.length > 0 && (
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            gap: "16px",
            marginTop: "-32px",
            marginBottom: "48px",
          }}>
            {highlights.map((h, i) => (
              <motion.div
                key={h.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1, ease: "easeOut" }}
                viewport={{ once: true, margin: "-60px" }}
                style={{
                  backgroundColor: "#ffffff",
                  borderRadius: "16px",
                  padding: "28px",
                  border: "1px solid #ede8df",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.07)",
                }}
              >
                {/* Accent line */}
                <div style={{
                  width: "32px",
                  height: "3px",
                  borderRadius: "999px",
                  backgroundColor: ORANGE,
                  marginBottom: "20px",
                }} />
                <h2 style={{
                  fontSize: "17px",
                  fontWeight: 700,
                  color: "#111827",
                  margin: "0 0 10px",
                  lineHeight: 1.3,
                }}>
                  {h.title}
                </h2>
                <p style={{
                  fontSize: "14px",
                  lineHeight: 1.7,
                  color: "#6B7280",
                  margin: 0,
                }}>
                  {h.description}
                </p>
              </motion.div>
            ))}
          </div>
        )}

        {/* Content sections */}
        {sections.length > 0 && (
          <div style={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            marginBottom: "48px",
          }}>
            {sections.map((section, idx) => (
              <motion.article
                key={section.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: idx * 0.07, ease: "easeOut" }}
                viewport={{ once: true, margin: "-60px" }}
                style={{
                  backgroundColor: "#ffffff",
                  borderRadius: "16px",
                  padding: "32px 36px",
                  border: "1px solid #ede8df",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                }}
              >
                <h2 style={{
                  fontSize: "22px",
                  fontWeight: 700,
                  color: "#111827",
                  margin: "0 0 16px",
                  lineHeight: 1.3,
                }}>
                  {section.title}
                </h2>

                {section.paragraphs?.map((p, i) => (
                  <p key={i} style={{
                    fontSize: "15px",
                    lineHeight: 1.75,
                    color: "#4B5563",
                    margin: i === 0 ? 0 : "12px 0 0",
                  }}>
                    {p}
                  </p>
                ))}

                {section.bullets?.length > 0 && (
                  <ul style={{
                    margin: "16px 0 0",
                    padding: 0,
                    listStyle: "none",
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                  }}>
                    {section.bullets.map((b, i) => (
                      <li key={i} style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
                        <span style={{
                          flexShrink: 0,
                          width: "6px",
                          height: "6px",
                          borderRadius: "50%",
                          backgroundColor: ORANGE,
                          marginTop: "9px",
                        }} />
                        <span style={{ fontSize: "15px", lineHeight: 1.75, color: "#4B5563" }}>
                          {b}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </motion.article>
            ))}
          </div>
        )}

        {/* Related links */}
        {relatedLinks.length > 0 && (
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            viewport={{ once: true, margin: "-60px" }}
            style={{
              backgroundColor: DARK,
              borderRadius: "16px",
              padding: "40px 44px",
            }}
          >
            <h2 style={{
              fontSize: "22px",
              fontWeight: 700,
              color: "#ffffff",
              margin: "0 0 24px",
            }}>
              Más información útil
            </h2>

            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
              gap: "12px",
            }}>
              {relatedLinks.map((linkItem) => (
                <Link
                  key={linkItem.to}
                  to={linkItem.to}
                  style={{ textDecoration: "none" }}
                >
                  <div style={{
                    borderRadius: "12px",
                    border: "1px solid rgba(255,255,255,0.12)",
                    backgroundColor: "rgba(255,255,255,0.05)",
                    padding: "18px 20px",
                    transition: "background-color 0.2s",
                    height: "100%",
                  }}>
                    <p style={{
                      fontSize: "15px",
                      fontWeight: 600,
                      color: "#ffffff",
                      margin: "0 0 8px",
                    }}>
                      {linkItem.label}
                    </p>
                    <p style={{
                      fontSize: "13px",
                      lineHeight: 1.6,
                      color: "rgba(255,255,255,0.58)",
                      margin: 0,
                    }}>
                      {linkItem.description}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </motion.section>
        )}

      </div>
    </main>
  );
};

export default PublicInfoPage;
